import type { Snapshot } from '../budget/repo'

export interface Account {
  id: string
  name: string
  isAdmin: boolean
}

export class OfflineError extends Error {
  constructor() {
    super('offline')
  }
}

/** Thrown when another device saved first. The caller reloads rather than clobbering. */
export class ConflictError extends Error {
  constructor(
    readonly version: number,
    readonly data: Snapshot
  ) {
    super('conflict')
  }
}

async function request(path: string, init?: RequestInit): Promise<Response> {
  try {
    return await fetch(path, { credentials: 'same-origin', ...init })
  } catch {
    throw new OfflineError()
  }
}

export async function login(code: string): Promise<Account | null> {
  const response = await request('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  })
  if (response.status === 401) return null
  if (!response.ok) throw new Error('Login failed')
  return ((await response.json()) as { account: Account }).account
}

export async function logout(): Promise<void> {
  await request('/api/auth/logout', { method: 'POST' })
}

export async function me(): Promise<Account | null> {
  const response = await request('/api/auth/me')
  if (!response.ok) return null
  return ((await response.json()) as { account: Account | null }).account
}

export async function getState(): Promise<{ version: number; data: Snapshot | null }> {
  const response = await request('/api/state')
  if (response.status === 401) throw new Error('unauthorised')
  if (!response.ok) throw new Error('Could not load your data')
  return (await response.json()) as { version: number; data: Snapshot | null }
}

/** Copies the spinner configuration into every other account. Returns how many. */
export async function publishGame(game: unknown): Promise<number> {
  const response = await request('/api/game/publish', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ game }),
  })
  if (!response.ok) throw new Error('Could not send the configuration')
  return ((await response.json()) as { updated: number }).updated
}

/** Sends the current savings balance to every other account. Returns how many. */
export async function publishSavings(currentFils: number): Promise<number> {
  const response = await request('/api/savings/publish', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ currentFils }),
  })
  if (!response.ok) throw new Error('Could not send the amount')
  return ((await response.json()) as { updated: number }).updated
}

export async function putState(version: number, data: Snapshot): Promise<number> {
  const response = await request('/api/state', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ version, data }),
  })

  if (response.status === 409) {
    const body = (await response.json()) as { version: number; data: Snapshot }
    throw new ConflictError(body.version, body.data)
  }
  if (response.status === 401) throw new Error('unauthorised')
  if (!response.ok) throw new Error('Could not save')

  return ((await response.json()) as { version: number }).version
}
